import { useState, useEffect, useRef, useMemo } from "react";
import {
  TextField,
  Autocomplete,
  InputAdornment,
  Stack,
  Button,
  Tooltip,
  Snackbar,
  Popper,
  Paper,
  Fade,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CopyAll as CopyIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { FixedSizeList as List } from "react-window";
import {
  setPage,
  setSelectedLogsPackage,
  setSelectedLogsProcessName,
  setSelectedLogsStatus,
  setSelectedLogsBrand,
  setSelectedLogsInterface,
  setSelectedRunMethodList,
  setSelectedStepList,
  setSelectedWorkFlow,
} from "../../../../../features/monitoringProcessLogsSlice";
import { useMonitoringProcessLogs } from "../../../../../customHooks/useMonitoringProcessLogs";

// Define types for filter options
interface FilterOption {
  id: string;
  label: string;
}

// Virtual list item component
const VirtualListItem = ({ index, style, data }: any) => {
  const { items, selectedItems, onToggle } = data;
  const item = items[index];

  return (
    <div style={style}>
      <FormControlLabel
        control={
          <Checkbox
            checked={selectedItems.some(
              (selectedId: string) => selectedId === String(item.id)
            )}
            onChange={() => onToggle(item.id)}
            color="secondary"
            size="small"
          />
        }
        label={item.label}
        sx={{
          width: "100%",
          margin: 0,
          padding: "4px 8px",
          "& .MuiFormControlLabel-label": {
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "200px",
          },
        }}
      />
    </div>
  );
};

const DcProcessLogsFilter = () => {
  const dispatch = useDispatch();
  const popperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<any>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchMonitoringProcessLogs } = useMonitoringProcessLogs();
  const {
    processLogsFilters,
    bigProcessLogsFilters,
    selectedProcessLogsDateRange,
    selectedLogsPackage,
    selectedLogsProcessName,
    selectedLogsStatus,
    selectedLogsBrand,
    selectedLogsInterface,
    selectedRunMethodList,
    selectedStepList,
    selectedWorkFlow,
    filtersLoading, // Add this line
    selectedPackageAutoComplete,
  } = useSelector((state: any) => state.monitoringProcessLogs);

  // UI states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentFilter, setCurrentFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>(
    {}
  ) as any;

  // Add click-away listener
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        setAnchorEl(null);
      }
    };

    if (anchorEl) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorEl]);

  useEffect(() => {
    setFilterOptions((prev) => ({
      package: {
        ...prev.package,
        data: Array.from(
          new Map(
            (bigProcessLogsFilters?.PACKAGE || []).map((item: any) => [
              item.id || item.value,
              { id: item.id || item.value, label: item.label },
            ])
          ).values()
        ),
      },
      processName: {
        ...prev.processName,
        data:
          processLogsFilters?.PROCESS_NAME?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [],
      },
      brand: {
        ...prev.brand,
        data:
          processLogsFilters?.BRAND?.map((item: any) => ({
            id: item.id || item.value,
            label: `${item.label} (${item.id || item.value})`,
          })) || [],
      },
      interface: {
        ...prev.interface,
        data:
          processLogsFilters?.INTERFACE?.map((item: any) => ({
            id: item.id || item.value,
            label: `${item.label} (${item.id || item.value})`,
          })) || [],
      },
      status: {
        ...prev.status,
        data:
          processLogsFilters?.STATUS?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [],
      },
      runMethod: {
        ...prev.runMethod,
        data:
          processLogsFilters?.RUN_METHOD?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [],
      },
      step: {
        ...prev.step,
        data:
          processLogsFilters?.STEP?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [],
      },
      workflowName: {
        ...prev.workflowName,
        data:
          bigProcessLogsFilters?.WORKFLOW_NAME?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [],
      },
    }));
  }, [processLogsFilters, bigProcessLogsFilters]);

  useEffect(() => {
    const initializeFiltersFromURL = () => {
      const updatedOptions = { ...filterOptions };

      // Update data options from processLogsFilters (if available)
      if (processLogsFilters && bigProcessLogsFilters) {
        updatedOptions.package.data = Array.from(
          new Map(
            (bigProcessLogsFilters?.PACKAGE || []).map((item: any) => [
              item.id || item.value,
              { id: item.id || item.value, label: item.label },
            ])
          ).values()
        );

        updatedOptions.processName.data =
          processLogsFilters?.PROCESS_NAME?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [];
        updatedOptions.brand.data =
          processLogsFilters?.BRAND?.map((item: any) => ({
            id: item.id || item.value,
            label: `${item.label} (${item.id || item.value})`,
          })) || [];
        updatedOptions.interface.data =
          processLogsFilters?.INTERFACE?.map((item: any) => ({
            id: item.id || item.value,
            label: `${item.label} (${item.id || item.value})`,
          })) || [];
        updatedOptions.status.data =
          processLogsFilters?.STATUS?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [];
        updatedOptions.runMethod.data =
          processLogsFilters?.RUN_METHOD?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [];
        updatedOptions.step.data =
          processLogsFilters?.STEP?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [];
        updatedOptions.workflowName.data =
          bigProcessLogsFilters?.WORKFLOW_NAME?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [];
      }

      // Set selected values from URL params (using IDs directly)
      Object.keys(updatedOptions).forEach((key) => {
        const filterKey =
          updatedOptions[key as keyof typeof updatedOptions].filterName;
        const paramValue = searchParams.get(filterKey);
        if (paramValue) {
          const ids = paramValue.split(",");
          updatedOptions[key as keyof typeof updatedOptions].selected = ids;
          // Dispatch the action to update Redux state
          updatedOptions[key as keyof typeof updatedOptions].action(ids);
        }
      });

      setFilterOptions(updatedOptions);

      // Set active filters based on URL params (using IDs directly)
      const activeFiltersFromURL: Record<string, string[]> = {};
      Object.keys(updatedOptions).forEach((key) => {
        const filterKey =
          updatedOptions[key as keyof typeof updatedOptions].filterName;
        const paramValue = searchParams.get(filterKey);
        if (paramValue) {
          activeFiltersFromURL[filterKey] = paramValue.split(",");
        }
      });
      setActiveFilters(activeFiltersFromURL);
    };

    initializeFiltersFromURL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processLogsFilters, searchParams]);

  // Filter options data
  const [filterOptions, setFilterOptions] = useState({
    package: {
      label: "Package",
      data:
        bigProcessLogsFilters?.PACKAGE?.map((item: any) => ({
          id: item.id || item.value,
          label: item.label,
        })) || [],
      selected: selectedLogsPackage || [],
      action: (value: any) => dispatch(setSelectedLogsPackage(value)),
      searchTerm: "",
      filterName: "package_name",
    },
    processName: {
      label: "Process Name",
      data:
        processLogsFilters?.PROCESS_NAME?.map((item: any) => ({
          id: item.id || item.value,
          label: item.label,
        })) || [],
      selected: selectedLogsProcessName || [],
      action: (value: any) => dispatch(setSelectedLogsProcessName(value)),
      searchTerm: "",
      filterName: "process_id",
    },
    status: {
      label: "Status",
      data:
        processLogsFilters?.STATUS?.map((item: any) => ({
          id: item.id || item.value,
          label: item.label,
        })) || [],
      selected: selectedLogsStatus || [],
      action: (value: any) => dispatch(setSelectedLogsStatus(value)),
      searchTerm: "",
      filterName: "status",
    },
    brand: {
      label: "Brand",
      data:
        processLogsFilters?.BRAND?.map((item: any) => ({
          id: String(item.id || item.value),
          label: `${item.label} (${item.id || item.value})`,
        })) || [],
      selected: selectedLogsBrand || [],
      action: (value: any) => dispatch(setSelectedLogsBrand(value)),
      searchTerm: "",
      filterName: "brand_id",
    },
    interface: {
      label: "Interface",
      data:
        processLogsFilters?.INTERFACE?.map((item: any) => ({
          id: String(item.id || item.value),
          label: `${item.label} (${item.id || item.value})`,
        })) || [],
      selected: selectedLogsInterface || [],
      action: (value: any) => dispatch(setSelectedLogsInterface(value)),
      searchTerm: "",
      filterName: "interface_id",
    },
    runMethod: {
      label: "Run Method",
      data:
        processLogsFilters?.RUN_METHOD?.map((item: any) => ({
          id: item.id || item.value,
          label: item.label,
        })) || [],
      selected: selectedRunMethodList || [],
      action: (value: any) => dispatch(setSelectedRunMethodList(value)),
      searchTerm: "",
      filterName: "run_method",
    },
    step: {
      label: "Step",
      data:
        processLogsFilters?.STEP?.map((item: any) => ({
          id: item.id || item.value,
          label: item.label,
        })) || [],
      selected: selectedStepList || [],
      action: (value: any) => dispatch(setSelectedStepList(value)),
      searchTerm: "",
      filterName: "step",
    },
    workflowName: {
      label: "Workflow Name",
      data:
        bigProcessLogsFilters?.WORKFLOW_NAME?.map((item: any) => ({
          id: item.id || item.value,
          label: item.label,
        })) || [],
      selected: selectedWorkFlow || [],
      action: (value: any) => dispatch(setSelectedWorkFlow(value)),
      searchTerm: "",
      filterName: "workflow_name",
    },
  });
  const [inputValue, setInputValue] = useState("");
  // State to track initial selected items when popup opens
  const [initialSelectedItems, setInitialSelectedItems] = useState<string[]>(
    []
  );

  // Memoized filtered and sorted data
  const filteredAndSortedData = useMemo(() => {
    if (!currentFilter) return [];

    const currentOptions =
      filterOptions[currentFilter as keyof typeof filterOptions];
    const filtered = currentOptions.data.filter((item: FilterOption) =>
      item.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort: initial selected items first (from when popup was opened), then alphabetically
    return [...filtered].sort((a: FilterOption, b: FilterOption) => {
      // Initial selected items come first
      const aInitiallySelected = initialSelectedItems.includes(String(a.id));
      const bInitiallySelected = initialSelectedItems.includes(String(b.id));

      if (aInitiallySelected && !bInitiallySelected) return -1;
      if (!aInitiallySelected && bInitiallySelected) return 1;

      // Then sort alphabetically
      return a.label.localeCompare(b.label);
    });
  }, [currentFilter, searchTerm, filterOptions, initialSelectedItems]);

  // Handle filter button click
  const handleFilterClick =
    (filterName: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
      // If clicking the same filter button that's already open, close it
      if (anchorEl && currentFilter === filterName) {
        setAnchorEl(null);
        setCurrentFilter("");
        return;
      }

      setCurrentFilter(filterName);
      setAnchorEl(event.currentTarget);
      setSearchTerm("");

      // Store the current selected items as initial state when opening
      const currentOptions =
        filterOptions[filterName as keyof typeof filterOptions];
      setInitialSelectedItems([...currentOptions.selected]);

      // Reset scroll position
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollToItem(0);
        }
      }, 100);
    };

  // Handle filter selection change
  const handleFilterChange = (id: string) => {
    const currentOptions =
      filterOptions[currentFilter as keyof typeof filterOptions];
    const filterName = currentOptions.filterName;

    // Calculate new selection for this filter (using IDs)
    const stringId = String(id);
    const newSelection = currentOptions.selected.includes(stringId)
      ? currentOptions.selected.filter((item: string) => item !== stringId)
      : [...currentOptions.selected, stringId];

    // Update filter options state
    setFilterOptions((prev) => ({
      ...prev,
      [currentFilter]: {
        ...prev[currentFilter as keyof typeof filterOptions],
        selected: newSelection,
      },
    }));

    // Dispatch the Redux action (with IDs)
    currentOptions.action(newSelection);

    // Update URL params (store IDs directly)
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", "0");
      if (newSelection.length > 0) {
        newParams.set(filterName, newSelection.join(","));
      } else {
        newParams.delete(filterName);
      }
      return newParams;
    });

    // Update active filters with IDs (for API calls)
    const updatedFilters = {
      ...activeFilters,
      [filterName]: newSelection.length > 0 ? newSelection : undefined,
    };

    // Clean up undefined filters
    const cleanedFilters = Object.fromEntries(
      Object.entries(updatedFilters).filter(([_, v]) => v !== undefined)
    );
    if (selectedPackageAutoComplete) {
      // If the rest of your filters use IDs but this value is a package name string,
      // send it as an array (API expects array for package_name)
      cleanedFilters.package_name = [selectedPackageAutoComplete];
    }
    setActiveFilters(cleanedFilters);

    // Make API call with ALL active filters (using IDs)
    const from_date = selectedProcessLogsDateRange[0].format("YYYY-MM-DD");
    const to_date = selectedProcessLogsDateRange[1].format("YYYY-MM-DD");

    fetchMonitoringProcessLogs(
      from_date,
      to_date,
      undefined,
      undefined,
      cleanedFilters
    );
  };

  // Refresh data
  const handleRefresh = () => {
    const from_date = selectedProcessLogsDateRange[0].format("YYYY-MM-DD");
    const to_date = selectedProcessLogsDateRange[1].format("YYYY-MM-DD");
    const filtersForRefresh = { ...activeFilters };
    if (selectedPackageAutoComplete) {
      filtersForRefresh.package_name = [selectedPackageAutoComplete];
    }
    fetchMonitoringProcessLogs(
      from_date,
      to_date,
      undefined,
      undefined,
      filtersForRefresh
    );

    dispatch(setPage(0));
    setSearchParams((prev) => {
      const updatedParams = new URLSearchParams(prev);
      updatedParams.set("page", "0");
      return updatedParams;
    });

    setSnackbarMessage("Data refreshed");
    setSnackbarOpen(true);
  };

  // Copy URL to clipboard
  const handleCopyURL = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setSnackbarMessage("URL copied to clipboard!");
      setSnackbarOpen(true);
    });
  };

  // Clear all filters
  // Clear all filters — but keep top "package" (selectedPackageAutoComplete) intact
  const clearAllFilters = () => {
    const from_date = selectedProcessLogsDateRange[0].format("YYYY-MM-DD");
    const to_date = selectedProcessLogsDateRange[1].format("YYYY-MM-DD");

    // 1) Build filtersForFetch: if package is selected, include it; otherwise empty
    const filtersForFetch: any = {};
    if (selectedPackageAutoComplete) {
      filtersForFetch.package_name = [selectedPackageAutoComplete];
    }

    // 2) Fetch with only package_name (if present). This keeps the table filtered by package.
    fetchMonitoringProcessLogs(
      from_date,
      to_date,
      undefined,
      undefined,
      filtersForFetch
    );

    // 3) Reset filter-card related Redux states (but SKIP package)
    Object.entries(filterOptions).forEach(([key, option]) => {
      if (key === "package") return; // keep package as it's managed by the top Autocomplete
      option.action([]); // dispatch action to reset this filter
    });

    // 4) Reset local filterOptions.selected for non-package keys
    setFilterOptions((prev) => {
      const resetOptions = { ...prev };
      Object.keys(resetOptions).forEach((key) => {
        if (key === "package") return; // keep package.selected intact
        resetOptions[key as keyof typeof resetOptions].selected = [];
      });
      return resetOptions;
    });

    // 5) Update activeFilters to only include package (if present) or none
    if (selectedPackageAutoComplete) {
      setActiveFilters({ package_name: [selectedPackageAutoComplete] });
    } else {
      setActiveFilters({});
    }

    // 6) Reset URL params BUT preserve package_name if it exists
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      // delete all filter params except package_name
      Object.keys(filterOptions).forEach((key) => {
        const fn = filterOptions[key as keyof typeof filterOptions].filterName;
        if (fn === "package_name") {
          // preserve package_name param if selectedPackageAutoComplete exists,
          // otherwise remove it
          if (selectedPackageAutoComplete) {
            newParams.set("package_name", selectedPackageAutoComplete);
          } else {
            newParams.delete("package_name");
          }
        } else {
          newParams.delete(fn);
        }
      });

      // keep/restore start/end if present in prev (or you can set them explicitly)
      newParams.set("page", "0");
      return newParams;
    });

    // 7) Reset UI snackbar
    setSnackbarMessage("All filters cleared");
    setSnackbarOpen(true);

    // Also reset Redux page
    dispatch(setPage(0));
  };

  // Check if any filter is applied
  // Check if any non-package filter is applied (we intentionally ignore the top "package")
  const isFilterApplied = Object.entries(filterOptions).some(
    ([key, option]) =>
      key !== "package" && option.selected && option.selected.length > 0
  );

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: "100%" }}
      >
        {/* Left side - Search and Filters */}
        <Stack direction="row" gap={2} alignItems="center">
          {/* Workflow Name Search */}
          <Autocomplete
            data-tracker="350"
            disablePortal
            size="small"
            sx={{ width: 300 }}
            options={filterOptions.workflowName.data}
            getOptionLabel={(option: FilterOption) => option.label || ""}
            inputValue={inputValue}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === "input") {
                setInputValue(newInputValue.trim()); 
              } else {
                setInputValue(newInputValue || "");
              }
            }}
            onChange={(_, newValue: FilterOption | null) => {
              const filterKey = filterOptions.workflowName.filterName;
              const newSelection = newValue ? [newValue.id] : [];

              dispatch(setSelectedWorkFlow(newSelection));

              // Update local state to reflect UI correctly
              setFilterOptions((prev) => ({
                ...prev,
                workflowName: {
                  ...prev.workflowName,
                  selected: newSelection,
                },
              }));

              setSearchParams((prev) => {
                const updatedParams = new URLSearchParams(prev);
                if (newValue) {
                  updatedParams.set(filterKey, newValue.id);
                  updatedParams.set("page", "0");
                } else {
                  updatedParams.delete(filterKey);
                }
                return updatedParams;
              });

              const updatedFilters = {
                ...activeFilters,
                [filterKey]: newSelection.length > 0 ? newSelection : undefined,
              };

              const cleanedFilters = Object.fromEntries(
                Object.entries(updatedFilters).filter(
                  ([_, v]) => v !== undefined
                )
              );

              setActiveFilters(cleanedFilters);

              const from_date =
                selectedProcessLogsDateRange[0].format("YYYY-MM-DD");
              const to_date =
                selectedProcessLogsDateRange[1].format("YYYY-MM-DD");

              fetchMonitoringProcessLogs(
                from_date,
                to_date,
                undefined,
                undefined,
                cleanedFilters
              );
            }}
            value={
              filterOptions.workflowName.data.find(
                (option: any) =>
                  option.id === filterOptions.workflowName.selected[0]
              ) || null
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Workflow Name"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
              />
            )}
          />

          {/* Filter Buttons */}
          {Object.entries(filterOptions)
            .filter(([key]) => key !== "workflowName" && key !== "package")
            .map(([key, option]) => (
              <Button
                key={key}
                sx={{
                  "&:hover": { backgroundColor: "rgba(156, 39, 176, 0.04)" },
                  textTransform: "capitalize",
                  minWidth: "auto",
                }}
                onClick={handleFilterClick(key)}
              >
                <span
                  style={{
                    color: option.selected.length > 0 ? "#9C27B0" : "#000000DE",
                    marginRight: option.selected.length > 0 ? "4px" : "0",
                  }}
                >
                  {option.label}
                </span>
                {option.selected.length > 0 ? (
                  <span style={{ color: "#9C27B0" }}>
                    +{option.selected.length}
                  </span>
                ) : (
                  <ArrowDownIcon
                    style={{
                      color:
                        anchorEl && currentFilter === key
                          ? "#9C2780"
                          : "#000000DE",
                      fontSize: "1rem",
                    }}
                  />
                )}
              </Button>
            ))}

          {/* Clear Filter Button */}
          <Button
            sx={{
              textTransform: "capitalize",
              fontWeight: 500,
              color: "#000000DE",
              "&:hover": {
                color: "#9C27B0",
                backgroundColor: "rgba(156, 39, 176, 0.04)",
              },
            }}
            onClick={clearAllFilters}
            disabled={!isFilterApplied}
          >
            Clear Filter
          </Button>
        </Stack>

        {/* Right side - Actions */}
        <Stack
          direction="row"
          gap={2}
          justifyContent={"flex-end"}
          paddingRight={1}
        >
          <Tooltip title="Copy Link">
            <CopyIcon
              sx={{ cursor: "pointer" }}
              color="secondary"
              onClick={handleCopyURL}
            />
          </Tooltip>
          <Tooltip title="Refresh">
            <RefreshIcon
              color="secondary"
              onClick={handleRefresh}
              sx={{ cursor: "pointer" }}
            />
          </Tooltip>
        </Stack>
      </Stack>

      {/* Enhanced Filter Popup with Virtual Scrolling */}
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        sx={{ zIndex: 1200 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper
              ref={popperRef}
              sx={{
                width: 300,
                maxHeight: 400,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {currentFilter && (
                <>
                  {/* Fixed Header */}
                  <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: 16, fontWeight: 500, mb: 1 }}
                    >
                      Select{" "}
                      {
                        filterOptions[
                          currentFilter as keyof typeof filterOptions
                        ].label
                      }
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      color="secondary"
                    />
                  </Box>

                  {/* Virtualized List */}
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    {filtersLoading ||
                    !bigProcessLogsFilters ||
                    bigProcessLogsFilters.length === 0 ? (
                      <Box
                        sx={{
                          p: 2,
                          textAlign: "center",
                          color: "text.secondary",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: 60,
                        }}
                      >
                        <Typography variant="body2">
                          Filters are loading. Please wait…
                        </Typography>
                      </Box>
                    ) : filteredAndSortedData.length > 0 ? (
                      <List
                        ref={listRef}
                        width={300}
                        height={Math.min(
                          300,
                          filteredAndSortedData.length * 40
                        )}
                        itemCount={filteredAndSortedData.length}
                        itemSize={40}
                        itemData={{
                          items: filteredAndSortedData,
                          selectedItems:
                            filterOptions[
                              currentFilter as keyof typeof filterOptions
                            ].selected,
                          onToggle: handleFilterChange,
                        }}
                      >
                        {VirtualListItem}
                      </List>
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          textAlign: "center",
                          color: "text.secondary",
                        }}
                      >
                        No options found
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Paper>
          </Fade>
        )}
      </Popper>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};

export default DcProcessLogsFilter;
